// Derivation for the Live Price Action tape (components/panel/PriceActionPanel).
//
// The tab accumulates an ephemeral, client-only series of quote snapshots. Each
// snapshot carries the CUMULATIVE regular-session volume at that instant —
// /stocks/quotes has no per-trade feed — so a per-entry volume has to be
// differenced out of the running total. That differencing has enough edge cases
// (session resets, volume-less pulses, quiet runs) to be worth isolating here
// rather than inlining in the render.

export interface Tick {
  t: number;
  price: number;
  /** Cumulative regular-session volume at this instant. Null when the provider
   *  reported none (pre-market on AV) or the tick came from a manual-refresh
   *  pulse, which carries only a price. */
  vol?: number | null;
}

export interface TapeRow {
  t: number;
  price: number;
  /** Price change vs the immediately preceding tick, rounded to display dp. */
  d: number;
  /** Shares traded since the previous REPORTING tick, or null when unknown. */
  dv: number | null;
}

const round2 = (x: number) => Math.round(x * 100) / 100;

/** Newest-first tape rows with per-entry volume differenced out of the running
 *  session total.
 *
 *  Volume is diffed against the last tick that actually REPORTED a total, not
 *  simply the previous tick: a volume-less manual-refresh pulse in the middle
 *  should widen the next entry's window, not blank out a real reading.
 *
 *  A negative difference means the session total reset under us (a new session,
 *  or a provider correction). That reports as null — unknown — rather than a
 *  nonsense negative share count. */
export function deriveTape(ticks: Tick[]): TapeRow[] {
  let lastCum: number | null = null;
  const rows: TapeRow[] = ticks.map((tick, i) => {
    const cum = typeof tick.vol === 'number' && Number.isFinite(tick.vol) ? tick.vol : null;
    let dv: number | null = null;
    if (cum != null && lastCum != null) {
      const d = cum - lastCum;
      dv = d >= 0 ? d : null;
    }
    if (cum != null) lastCum = cum;
    return {
      t: tick.t,
      price: tick.price,
      d: i === 0 ? 0 : round2(tick.price - ticks[i - 1].price),
      dv,
    };
  });
  return rows.reverse();
}

/** Baseline for the RVol column: the median of the entries that actually
 *  traded.
 *
 *  Median rather than mean because a single burst would drag a mean upward and
 *  flatten every ratio toward 1. Zero-volume entries are excluded because the
 *  tape is mostly quiet — the client polls faster than the backend's ~20s quote
 *  coalescing, so repeat snapshots are common — and including them would pin
 *  the baseline at 0 and make every ratio infinite.
 *
 *  Null until at least `minSamples` active entries exist, so an early tape
 *  doesn't publish ratios computed against one or two observations. */
export function medianActiveVolume(rows: TapeRow[], minSamples = 3): number | null {
  const xs = rows
    .map((r) => r.dv)
    .filter((v): v is number => v != null && v > 0)
    .sort((a, b) => a - b);
  if (xs.length < minSamples) return null;
  const m = xs.length >> 1;
  return xs.length % 2 ? xs[m] : (xs[m - 1] + xs[m]) / 2;
}

/** This entry's volume against the session's typical active entry. Null when
 *  the entry didn't trade or there's no baseline yet. */
export function relativeVolume(dv: number | null, typical: number | null): number | null {
  if (dv == null || dv <= 0 || typical == null || typical <= 0) return null;
  const r = dv / typical;
  return Number.isFinite(r) ? r : null;
}

/** Compact share count — tape rows are ~11px across five columns, so a raw
 *  1234567 would break the layout. */
export function fmtVol(v: number): string {
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return String(Math.round(v));
}
